// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { lazy, Suspense } from "react"
import { Routes, Route, Navigate, Outlet } from "react-router-dom"
import LandingPage from "./LandingPage"
import AuthCallbackPage from "./AuthCallbackPage"
import GuideHubPage from "./guides/GuideHubPage"
import LegalPage, { LegacyLegalRedirect } from "./LegalPage"
import SaveYourDayPage from "./guides/SaveYourDayPage"
import WhyTheScaleLiesPage from "./guides/WhyTheScaleLiesPage"
import GlossaryPage from "./guides/GlossaryPage"
import HowYourGroceryBudgetWorksPage from "./guides/HowYourGroceryBudgetWorksPage"
import TodayPage from "./app/TodayPage"
import PlanPage from "./app/PlanPage"
import RecipePage from "./app/RecipePage"
import PantryPage from "./app/PantryPage"
import ShopPage from "./app/ShopPage"
import InsightsPage from "./app/InsightsPage"
import AccessPage from "./app/AccessPage"
import { OnboardingPage } from "./onboarding"
import { PlanReadyPage } from "./app/PlanReadyPage"
import { moreRoutes } from "./more"
import { RequireAuth } from "@/components/auth/RequireAuth"
import { useAuth } from "@/hooks/useAuth"
import { AppShell } from "@/components/layout/AppShell"
import { CONTACT_PATH, LEGAL_PATHS } from "@/lib/legalRoutes"
import { RequireOnboardingComplete } from "@/components/onboarding/RequireOnboardingComplete"

/**
 * Dev-only design prototype. The conditional matters: `lazy()` at module
 * scope runs unconditionally, so a bare `lazy(() => import(...))` emits its
 * chunk into the PRODUCTION bundle even when the route below is guarded —
 * verified by building and grepping the output. Behind `import.meta.env.DEV`
 * the whole expression becomes `false ? ... : null`, the dynamic import is
 * dead code, and neither the chunk nor the route path survives the build.
 */
const TodayV2Prototype = import.meta.env.DEV
  ? lazy(() => import("./design/TodayV2Prototype"))
  : null

/**
 * T11 adds /app/onboarding (full-page, no AppShell chrome) and the
 * RequireOnboardingComplete guard: every other /app/* screen redirects to
 * onboarding until GET /v1/me reports onboardingStatus "completed". The
 * onboarding route itself sits outside that guard so it's never redirected
 * to itself, and outside AppShell so the bottom-nav/left-rail/Amy button
 * never competes with the onboarding flow.
 */
/**
 * A signed-in visitor landing on "/" (new tab, bookmark, typed URL) goes
 * straight into the app instead of seeing the marketing page again —
 * ADR-020's "seamless second tab" behavior. While the restored session is
 * still being validated (status "loading") the public landing renders; it
 * is public content, so briefly showing it to a soon-to-be-redirected
 * user is harmless and avoids a spinner for every anonymous visitor.
 */
function LandingOrApp() {
  const { status } = useAuth()
  if (status === "signedIn") return <Navigate to="/app" replace />
  return <LandingPage />
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingOrApp />} />

      {/* Today v2 DESIGN PROTOTYPE (2026-09-06) — isolated interaction
          harness, NOT production Today. Registered only when
          `import.meta.env.DEV` is true: Vite replaces that constant with
          `false` in a production build, so this branch and the lazily
          imported module are dropped from the bundle entirely and the route
          cannot be reached by an ordinary user. Fixture state only — no API
          client, no auth guard, no Amy, no writes. Nothing links to it. */}
      {import.meta.env.DEV && TodayV2Prototype && (
        <Route
          path="/design/today-v2"
          element={
            <Suspense fallback={null}>
              <TodayV2Prototype />
            </Suspense>
          }
        />
      )}

      {/* Social sign-in OAuth redirect target (public; registered as a
          Cognito callback URL only when a provider flag is enabled). */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Legal documents, one real URL each (footer + auth dialog link
          here). The old /legal/* paths redirect to them. */}
      <Route path={LEGAL_PATHS.privacy} element={<LegalPage panel="privacy" />} />
      <Route path={LEGAL_PATHS.terms} element={<LegalPage panel="terms" />} />
      <Route path={LEGAL_PATHS.imprint} element={<LegalPage panel="imprint" />} />
      <Route path="/legal/:panel" element={<LegacyLegalRedirect />} />
      <Route path={CONTACT_PATH} element={<LandingPage />} />

      {/* Guide Center hub — public index of every guide. Linked from the
          landing header strip/footer and the app's More hub. */}
      <Route path="/guides" element={<GuideHubPage />} />

      {/* ADR-026: public guide, no auth, no personal context. The landing
          page links here; this route stands alone otherwise. */}
      <Route path="/guides/save-your-day" element={<SaveYourDayPage />} />

      {/* ADR-027 rule 8: public guide, no auth, no personal context. Links
          back to the save-your-day guide from its own related-reading spot
          (SaveYourDayPage has no such spot to link the other way). */}
      <Route path="/guides/why-the-scale-lies" element={<WhyTheScaleLiesPage />} />

      {/* Consumer Budget Guide (2026-09-05): the money concepts explained in
          plain language, linked from the Budget settings and the hub. */}
      <Route path="/guides/how-your-grocery-budget-works" element={<HowYourGroceryBudgetWorksPage />} />

      {/* Budget Model v2 (ADR-052 §12): the public Guide Center Glossary —
          the deep-link target of every ⓘ "Learn more" (#<term-id>). */}
      <Route path="/guides/glossary" element={<GlossaryPage />} />

      <Route
        path="/app/onboarding"
        element={
          <RequireAuth>
            <OnboardingPage />
          </RequireAuth>
        }
      />

      {/* Pre-hook value reveal between onboarding finish and activation —
          full-page like onboarding, no AppShell chrome. */}
      <Route
        path="/app/plan-ready"
        element={
          <RequireAuth>
            <PlanReadyPage />
          </RequireAuth>
        }
      />

      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="today" replace />} />
        <Route element={<RequireOnboardingComplete />}>
          <Route path="today" element={<TodayPage />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="recipes/:mealId" element={<RecipePage />} />
          <Route path="pantry" element={<PantryPage />} />
          <Route path="shop" element={<ShopPage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="access" element={<AccessPage />} />
          <Route path="more" element={<Outlet />}>
            {moreRoutes}
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
